import { useEffect, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, List, ListItem, Typography, useTheme } from "@mui/material";
import { CarCrash, ExpandMore } from "@mui/icons-material";
import moment from "moment";
import { LeasmanAccident, LeasmanAccidentStatus, ThemeColor } from "../../system/Domain";
import Tile from "../../Components/Tiles/Tile";
import TileContent from "../../Components/Tiles/TileContent";
import ListItemChip from "../../Components/ListItemChip";
import dateFormats from "../../system/dateFormats";
import formatCurrency from "../../system/formatCurrency";

interface Props {
	accidents: LeasmanAccident[];
}

const statusSettings: Record<LeasmanAccidentStatus, { label: string, color: ThemeColor }> = {
	Created: { label: "Erstellt", color: "info" },
	Active: { label: "In Bearbeitung", color: "warning" },
	Closed: { label: "Abgeschlossen", color: "success" },
	Lawyer: { label: "Anwaltlich", color: "error" }
};

const formatAccidentDateRange = (accident: LeasmanAccident): string => {
	const from = moment(accident.date).format(dateFormats.date);

	if (!accident.dateClosed) {
		return `seit ${from}`;
	}

	return `${from} – ${moment(accident.dateClosed).format(dateFormats.date)}`;
};

const VehicleAccidentsTile = ({ accidents }: Props) => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState<Record<number, boolean>>({});

	// Newest accidents are the most relevant to a reviewer, so show them first, mirroring the
	// vehicle history tile.
	const sortedAccidents = useMemo(
		() => [...(accidents ?? [])].sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()),
		[accidents]
	);

	// Start with every entry expanded so the user sees all recorded damages at a glance;
	// they can then collapse the ones that aren't relevant to their review.
	useEffect(() => {
		setExpanded(Object.fromEntries(sortedAccidents.map(a => [a.id, true])));
	}, [sortedAccidents]);

	const totalDamages = sortedAccidents.reduce((sum, a) => sum + (a.damages?.length ?? 0), 0);

	const toggle = (id: number) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

	return (
		<Tile
			title="Unfälle & Schäden"
			subtitle={sortedAccidents.length > 0
				? `${sortedAccidents.length} Unfälle · ${totalDamages} Schäden`
				: undefined
			}
		>
			<TileContent dense>
				{sortedAccidents.length === 0 && (
					<Box p={2} sx={{ opacity: 0.6, fontSize: "0.8rem" }}>
						Keine Unfälle in Leasman erfasst
					</Box>
				)}

				{sortedAccidents.map(accident => {
					const status = statusSettings[accident.status] ?? { label: accident.status, color: "info" as ThemeColor };
					const damageCount = accident.damages?.length ?? 0;

					return (
						<Accordion
							key={accident.id}
							disableGutters
							expanded={!!expanded[accident.id]}
							onChange={() => toggle(accident.id)}
							sx={{
								boxShadow: "none",
								"&:before": { display: "none" },
								"&.Mui-expanded": { margin: 0 },
								borderBottom: `1px solid ${theme.palette.divider}`,
								"&:last-of-type": { borderBottom: "none" }
							}}
						>
							<AccordionSummary expandIcon={<ExpandMore />}>
								<Box sx={{
									display: "flex",
									flexWrap: "wrap",
									alignItems: "center",
									columnGap: theme.spacing(1),
									rowGap: theme.spacing(0.5),
									width: "100%",
									paddingRight: theme.spacing(1)
								}}>
									<CarCrash fontSize="small" sx={{ opacity: 0.5 }} />

									<Typography variant="body2" sx={{ minWidth: 165 }}>
										{formatAccidentDateRange(accident)}
									</Typography>

									<ListItemChip label={status.label} color={status.color} />

									{accident.fullKasko && <ListItemChip label="Vollkasko" color="info" />}
									{accident.partialKasko && <ListItemChip label="Teilkasko" color="info" />}
									{accident.liability && <ListItemChip label="Haftpflicht" color="info" />}

									<Typography
										variant="caption"
										sx={{
											opacity: 0.7,
											whiteSpace: "nowrap",
											flexBasis: "100%"
										}}
									>
										{damageCount} {damageCount === 1 ? "Schaden" : "Schäden"}{" "}
										{formatCurrency(accident.expectedDamageAmount, "– €")}
									</Typography>
								</Box>
							</AccordionSummary>
							<AccordionDetails sx={{ paddingTop: 0 }}>
								<List dense disablePadding>
									{accident.damages?.map(damage => (
										<ListItem key={damage.id} disableGutters sx={{ paddingLeft: theme.spacing(4.5) }}>
											<Typography variant="body2">{damage.type}</Typography>
										</ListItem>
									))}
									{damageCount === 0 && (
										<ListItem disableGutters sx={{ paddingLeft: theme.spacing(4.5) }}>
											<Typography variant="body2" sx={{ opacity: 0.6 }}>Keine Schäden erfasst</Typography>
										</ListItem>
									)}
								</List>
							</AccordionDetails>
						</Accordion>
					);
				})}
			</TileContent>
		</Tile>
	);
};

export default VehicleAccidentsTile;
